import { useMemo, useEffect, useState } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { fmtBRL, fmtNum } from "@/lib/format";
import { Tag, Database, Search, ArrowDownUp, Box, Layers, History, ArrowLeft, ArrowRight, Activity } from "lucide-react";
import { cn } from "@/lib/utils";
import { db } from "@/lib/local/db";

const PAGE = 50;

export default function CfopPage() {
  const { dashboard } = useFiscalStore();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [cfops, setCfops] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => { document.title = "Classificação CFOP — DataGrid XML"; }, []);

  useEffect(() => {
    async function loadCfops() {
      setIsLoading(true);
      try {
        const allItens = await db.itens.toArray();
        const map = new Map();

        allItens.forEach(item => {
          const key = item.cfop;
          if (!map.has(key)) {
            map.set(key, { cfop: key, count: 0, valor: 0, desc: "OPERAÇÃO FISCAL " + key });
          }
          const entry = map.get(key);
          entry.count++;
          entry.valor += item.vProd;
        });

        setCfops(Array.from(map.values()).sort((a, b) => b.valor - a.valor));
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
    loadCfops();
  }, []);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return cfops.filter(c => c.cfop.toLowerCase().includes(ql));
  }, [cfops, q]);

  const slice = filtered.slice(page * PAGE, (page + 1) * PAGE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));

  return (
    <div className="p-4 lg:p-6 max-w-[1600px] mx-auto space-y-6 animate-in fade-in duration-1000">
      <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 border-l-4 border-primary pl-8 py-4 mb-10 bg-white/[0.02] glass-technical border-y-0 border-r-0 rounded-r-3xl">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Tag className="size-3 text-primary animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-primary">Fiscal Classification Index</span>
          </div>
          <h1 className="text-5xl font-black tracking-tighter leading-none mb-2">
            Classificação <span className="text-primary">CFOP</span>
          </h1>
          <p className="text-[12px] text-muted-foreground font-black uppercase tracking-[0.1em] mt-2 flex items-center gap-6 opacity-70">
             <span className="flex items-center gap-2 uppercase tabular-nums"><Layers className="size-3.5 text-primary" /> {fmtNum(cfops.length)} CÓDIGOS IDENTIFICADOS</span>
             <span className="flex items-center gap-2 uppercase tabular-nums"><Activity className="size-3.5 text-emerald-500" /> Aggregation Engine Active</span>
          </p>
        </div>
      </header>

      <div className="glass-card p-6 border-primary/5 bg-muted/5">
        <div className="relative group">
          <Search className="size-4 absolute left-5 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" />
          <input 
            placeholder="Buscar por Código CFOP..." 
            value={q} 
            onChange={(e) => { setQ(e.target.value); setPage(0); }} 
            className="input-premium w-full pl-14 h-14 rounded-2xl bg-white/10 border-foreground/5 text-xs font-bold uppercase tracking-tight"
          />
        </div>
      </div>

      <div className="glass-card overflow-hidden border-primary/5">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-muted/10 text-muted-foreground border-b border-foreground/5">
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] border-r border-foreground/5">Código CFOP</th>
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] border-r border-foreground/5">Descrição Operacional</th>
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] text-right border-r border-foreground/5">Ocorrências</th>
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] text-right">Volume Financeiro</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-foreground/[0.03]">
              {slice.map((c) => (
                <tr key={c.cfop} className="hover:bg-primary/[0.05] transition-all group">
                  <td className="p-5 border-r border-foreground/[0.03]">
                    <div className="flex items-center gap-3">
                      <div className="size-10 rounded-xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center font-black text-sm shadow-sm group-hover:scale-110 transition-transform">
                        {c.cfop}
                      </div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Natureza Operação</div>
                    </div>
                  </td>
                  <td className="p-5 border-r border-foreground/[0.03] text-xs font-black uppercase tracking-tight text-muted-foreground/80">
                    {c.desc}
                  </td>
                  <td className="p-5 border-r border-foreground/[0.03] text-right font-black text-sm tabular-nums">
                    {fmtNum(c.count)}
                  </td>
                  <td className="p-5 text-right font-black text-primary tabular-nums text-lg tracking-tighter">
                    {fmtBRL(c.valor)}
                  </td>
                </tr>
              ))}
              {slice.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={4} className="p-32 text-center">
                    <div className="flex flex-col items-center justify-center gap-4 opacity-10 animate-pulse">
                      <Box className="size-16" />
                      <span className="text-sm font-black uppercase tracking-[0.4em]">Zero CFOP Registers</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        <div className="flex flex-col sm:flex-row items-center justify-between p-6 bg-muted/10 border-t border-primary/10">
          <div className="flex items-center gap-4">
            <div className="size-10 rounded-xl glass-technical border-primary/20 flex items-center justify-center text-primary">
               <History className="size-5" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60">
              Registros <span className="text-foreground">{(page * PAGE) + 1}—{Math.min((page + 1) * PAGE, filtered.length)}</span> de {fmtNum(filtered.length)}
            </span>
          </div>

          <div className="flex items-center gap-6 mt-4 sm:mt-0">
            <div className="flex gap-2">
              <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="btn-glass size-12 flex items-center justify-center rounded-2xl disabled:opacity-10 hover:border-primary/40 transition-all">
                <ArrowLeft className="size-5" />
              </button>
              <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)} className="btn-glass size-12 flex items-center justify-center rounded-2xl disabled:opacity-10 hover:border-primary/40 transition-all">
                <ArrowRight className="size-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
